import type { ReactNode } from "react";
import styles from "./SetupWizard.module.css";

export type InstallSceneKind = "router" | "power" | "installer" | "panel";

// The Shobdo Edge appliance wireframe, geometry lifted from the videorag.shobdo.ai
// marketing illustration (same isometric slab, ports, power button, status LED and
// engraved label) minus its hatching noise, so the scenes here read as the same device
// the customer saw on the site — and the one that arrives in the box.
const DEVICE_OUTLINE =
  "M 205.9 43.2 Q 221.9 34.0 237.8 43.2 L 472.8 178.9 Q 488.8 188.1 488.8 206.5 " +
  "L 488.8 250.1 Q 488.8 268.5 472.8 277.7 L 324.9 363.1 Q 308.9 372.4 292.9 363.1 " +
  "L 58.0 227.5 Q 42.0 218.2 42.0 199.8 L 42.0 156.3 Q 42.0 137.9 58.0 128.6 Z";
const DEVICE_TOP_FACE =
  "M 221.9 34.0 L 472.8 197.3 L 320.4 285.3 Q 308.9 291.9 297.4 285.3 L 58.0 147.1 Z";
const EDGE_LINES = [
  "M 58.0 147.1 L 297.4 285.3",
  "M 472.8 197.3 L 320.4 285.3",
  "M 308.9 353.9 L 308.9 305.2",
];
// Two RJ45-style ports on the right face; ETH_PORT is the one the cable plugs into.
const ETH_PORT =
  "M 401.7 289.3 Q 401.7 295.3 396.5 298.3 L 366.3 315.7 Q 361.1 318.8 361.1 312.7 " +
  "L 361.1 294.6 Q 361.1 288.6 366.3 285.6 L 396.5 268.2 Q 401.7 265.1 401.7 271.2 Z";
const ETH_PORT_2 =
  "M 459.8 255.8 Q 459.8 261.8 454.5 264.8 L 424.4 282.2 Q 419.1 285.2 419.1 279.2 " +
  "L 419.1 261.1 Q 419.1 255.1 424.4 252.1 L 454.5 234.7 Q 459.8 231.7 459.8 237.7 Z";
// Small ports on the left face; POWER_PORT is the one the adapter plugs into.
const POWER_PORT =
  "M 76.8 208.9 Q 76.8 214.9 82.0 217.9 L 97.7 227.0 Q 102.9 230.0 102.9 223.9 " +
  "L 102.9 215.9 Q 102.9 209.9 97.7 206.9 L 82.0 197.8 Q 76.8 194.8 76.8 200.8 Z";
const SIDE_PORTS = [
  "M 117.4 232.3 Q 117.4 238.4 122.7 241.4 L 141.2 252.1 Q 146.4 255.1 146.4 249.1 " +
    "L 146.4 241.0 Q 146.4 235.0 141.2 232.0 L 122.7 221.3 Q 117.4 218.2 117.4 224.3 Z",
  "M 160.9 257.4 Q 160.9 263.5 166.2 266.5 L 184.7 277.2 Q 190.0 280.2 190.0 274.2 " +
    "L 190.0 266.2 Q 190.0 260.1 184.7 257.1 L 166.2 246.4 Q 160.9 243.4 160.9 249.4 Z",
];
// Pin ticks hanging from each ethernet port's top edge — they read as RJ45 jacks
// (without them the rounded cutouts read as USB).
const ETH_PIN_TICKS = [
  "M 372.3 282.1 v 6",
  "M 378.4 278.6 v 6",
  "M 384.4 275.2 v 6",
  "M 390.5 271.7 v 6",
  "M 430.4 248.6 v 6",
  "M 436.4 245.1 v 6",
  "M 442.4 241.7 v 6",
  "M 448.5 238.2 v 6",
];
const VENTS = [
  "M 385.8 160.5 L 432.2 187.3",
  "M 380.3 163.6 L 426.7 190.4",
  "M 374.8 166.8 L 421.2 193.6",
  "M 369.2 170.0 L 415.7 196.8",
  "M 363.7 173.2 L 410.1 200.0",
  "M 358.2 176.4 L 404.6 203.2",
];

type DeviceProps = {
  /** off = dark dot · settling = blink-then-solid loop (power scene) · on = solid green */
  led: "off" | "settling" | "on";
  /** Pressing ripple on the power button (power scene). */
  pressButton?: boolean;
};

/** The appliance itself, drawn in its native 531×428 coordinate space. */
const EdgeDevice = ({ led, pressButton = false }: DeviceProps) => (
  <g className={styles.deviceInk}>
    <path
      d={DEVICE_OUTLINE}
      className={styles.deviceShell}
      strokeWidth={2.2}
      strokeLinejoin="round"
    />
    <path d={DEVICE_TOP_FACE} className={styles.deviceFaceTint} />
    {EDGE_LINES.map((d) => (
      <path key={d} d={d} strokeWidth={2} strokeLinecap="round" fill="none" />
    ))}
    {[ETH_PORT, ETH_PORT_2, POWER_PORT, ...SIDE_PORTS].map((d) => (
      <path key={d} d={d} strokeWidth={1.5} strokeLinejoin="round" fill="none" />
    ))}
    {ETH_PIN_TICKS.map((d) => (
      <path key={d} d={d} strokeWidth={1} strokeLinecap="round" opacity={0.7} fill="none" />
    ))}
    {VENTS.map((d) => (
      <path key={d} d={d} strokeWidth={1} strokeLinecap="round" opacity={0.65} fill="none" />
    ))}
    <text
      x="0"
      y="4.5"
      transform="matrix(0.866 0.5 -0.866 0.5 262.5 164.7)"
      className={styles.deviceLabel}
      textAnchor="middle"
    >
      Shobdo Edge Agent
    </text>
    <circle cx="308.9" cy="291.3" r="1.1" fill="currentColor" stroke="none" />
    {/* Power button */}
    <circle
      className={pressButton ? styles.buttonPress : undefined}
      cx="207.4"
      cy="292.0"
      r="4"
      fill="var(--color-bg, #fff)"
      strokeWidth={1.5}
    />
    {pressButton && (
      <circle
        className={styles.buttonRipple}
        cx="207.4"
        cy="292.0"
        r="4"
        fill="none"
        strokeWidth={1.2}
      />
    )}
    {/* Status LED */}
    <circle
      className={
        led === "on" ? styles.ledOn : led === "settling" ? styles.ledSettling : styles.ledOff
      }
      cx="262.5"
      cy="295.3"
      r="2.8"
      stroke="none"
    />
    <circle
      className={led === "off" ? styles.ledHaloOff : styles.ledHalo}
      cx="262.5"
      cy="295.3"
      r="6"
      fill="none"
      strokeWidth={1}
    />
  </g>
);

const SceneSvg = ({ label, children }: { label: string; children: ReactNode }) => (
  <svg
    className={styles.sceneSvg}
    viewBox="0 0 760 430"
    role="img"
    aria-label={label}
    fill="none"
    stroke="currentColor"
  >
    {children}
  </svg>
);

const CaptionLine = ({ d }: { d: string }) => (
  <path d={d} strokeWidth={0.8} strokeDasharray="3 3" opacity={0.6} fill="none" />
);

/** RJ45 plug pointing towards the port (+x): strain-relief boot, latch, nose, pins. */
const Rj45Plug = () => (
  <>
    <rect
      x="-40"
      y="-5.5"
      width="10"
      height="11"
      rx="3"
      strokeWidth={1.5}
      fill="var(--color-bg, #fff)"
    />
    <path
      d="M -6 -10 L -10 -14 L -22 -14 L -26 -10"
      strokeWidth={1.5}
      strokeLinejoin="round"
      fill="var(--color-bg, #fff)"
    />
    <rect
      x="-30"
      y="-10"
      width="30"
      height="20"
      rx="2"
      strokeWidth={2}
      fill="var(--color-bg, #fff)"
    />
    <path d="M 0 -7 L 8 -7 L 8 7 L 0 7" strokeWidth={2} fill="var(--color-bg, #fff)" />
    <line x1="2" y1="-4.8" x2="7" y2="-4.8" strokeWidth={1} />
    <line x1="2" y1="-1.6" x2="7" y2="-1.6" strokeWidth={1} />
    <line x1="2" y1="1.6" x2="7" y2="1.6" strokeWidth={1} />
    <line x1="2" y1="4.8" x2="7" y2="4.8" strokeWidth={1} />
  </>
);

/** Wireframe store router: front panel, two antennas, a row of LAN ports. */
const Router = () => (
  <g className={styles.deviceInk}>
    <rect
      x="52"
      y="96"
      width="150"
      height="46"
      rx="8"
      strokeWidth={2}
      fill="var(--color-bg, #fff)"
    />
    <line x1="76" y1="96" x2="70" y2="58" strokeWidth={1.5} strokeLinecap="round" />
    <circle cx="69.2" cy="53.5" r="2.4" strokeWidth={1.5} />
    <line x1="178" y1="96" x2="184" y2="58" strokeWidth={1.5} strokeLinecap="round" />
    <circle cx="184.8" cy="53.5" r="2.4" strokeWidth={1.5} />
    {[0, 1, 2, 3].map((i) => (
      <rect key={i} x={66 + i * 21} y="118" width="14" height="12" rx="2" strokeWidth={1.2} />
    ))}
    <circle cx="186" cy="107" r="2" fill="currentColor" stroke="none" opacity={0.7} />
  </g>
);

/**
 * Scene 1 — the ethernet cable runs from a free LAN port on the store router, loops with
 * slack, and its RJ45 plug seats into the device's ethernet port; the port's link light
 * blinks once seated. The whole cycle loops.
 */
const RouterScene = () => (
  <SceneSvg label="Animation: ethernet cable connecting the store router to the edge agent">
    <g transform="translate(255 48) scale(0.92)">
      <EdgeDevice led="off" />
    </g>
    {/* Cable + plug are ONE group, so they can never separate: the whole assembly
        nudges into the port to seat, and the cable's router end hides under the
        router body (drawn after this group), so no joint is ever visible. */}
    <g className={styles.plugAssembly}>
      <path
        className={styles.cablePath}
        d="M 115 115 L 115 130 C 115 215, 155 305, 265 358 C 350 398, 440 400, 490 385 L 570 338.5"
        strokeWidth={2.2}
        strokeLinecap="round"
        fill="none"
        pathLength={100}
      />
      <g className={styles.plugFade} transform="translate(599 321) rotate(-31)">
        <Rj45Plug />
      </g>
    </g>
    <Router />
    {/* Captions */}
    <g className={styles.sceneLabels}>
      <text x="52" y="34">
        Store router — any free LAN port
      </text>
      <CaptionLine d="M 100 40 L 110 90" />
      <text x="340" y="418">
        Ethernet cable
      </text>
      <CaptionLine d="M 388 408 L 392 394" />
      <text x="752" y="250" textAnchor="end">
        Ethernet port
      </text>
      <CaptionLine d="M 720 258 L 612 310" />
    </g>
  </SceneSvg>
);

/**
 * Scene 2 — the power adapter plugs into a wall outlet, its connector seats into the
 * device's power port, the power button is pressed, and the status light blinks and then
 * settles solid green. One looping timeline drives all four phases in order.
 */
const PowerScene = () => (
  <SceneSvg label="Animation: powering on the edge agent — adapter in, button pressed, status light settles">
    <g transform="translate(255 48) scale(0.92)">
      <EdgeDevice led="settling" pressButton />
    </g>
    {/* The ethernet cable from step 1 stays connected — drawn small, running off right */}
    <g className={styles.deviceInk}>
      <path
        d="M 565 342 C 548 352, 580 368, 650 366 C 700 364, 734 356, 758 346"
        strokeWidth={2.2}
        strokeLinecap="round"
        fill="none"
      />
      <g transform="translate(599 321) rotate(-31)">
        <Rj45Plug />
      </g>
    </g>
    {/* Stub, adapter, cable and connector are ONE group, so they can never separate:
        the assembly nudges into the power port; the stub's outlet end hides under the
        outlet plate (drawn after this group). */}
    <g className={styles.pwrAssembly}>
      <path
        className={styles.pwrCable}
        d="M 100 350 L 126 348 M 170 340 C 230 320, 270 292, 315 260"
        strokeWidth={2.2}
        strokeLinecap="round"
        fill="none"
      />
      <rect
        x="128"
        y="336"
        width="44"
        height="26"
        rx="5"
        strokeWidth={1.8}
        fill="var(--color-bg, #fff)"
        transform="rotate(-12 150 349)"
      />
      <g transform="translate(330 249) rotate(-36)">
        {/* USB-style power plug pointing towards the port (+x) */}
        <rect
          x="-32"
          y="-5"
          width="8"
          height="10"
          rx="2.5"
          strokeWidth={1.5}
          fill="var(--color-bg, #fff)"
        />
        <rect
          x="-24"
          y="-8"
          width="24"
          height="16"
          rx="3"
          strokeWidth={2}
          fill="var(--color-bg, #fff)"
        />
        <path d="M 0 -5 L 8 -5 L 8 5 L 0 5" strokeWidth={2} fill="var(--color-bg, #fff)" />
        <rect x="2" y="-2.4" width="4.5" height="4.8" strokeWidth={1} />
      </g>
    </g>
    {/* The ethernet port's link light wakes once the device powers on */}
    <circle className={styles.pwrLink} cx="630" cy="296" r="3.2" stroke="none" />
    {/* Wall outlet, over the stub's end */}
    <g className={styles.deviceInk}>
      <rect
        x="56"
        y="316"
        width="52"
        height="76"
        rx="8"
        strokeWidth={2}
        fill="var(--color-bg, #fff)"
      />
      <line x1="74" y1="340" x2="74" y2="352" strokeWidth={2.4} strokeLinecap="round" />
      <line x1="90" y1="340" x2="90" y2="352" strokeWidth={2.4} strokeLinecap="round" />
      <circle cx="82" cy="368" r="3.4" strokeWidth={1.5} />
    </g>
    <g className={styles.sceneLabels}>
      <text x="56" y="304">
        Wall outlet
      </text>
      <CaptionLine d="M 82 308 L 82 314" />
      <text x="248" y="216" textAnchor="end">
        Power port
      </text>
      <CaptionLine d="M 252 222 L 326 242" />
      <text x="404" y="396">
        Power button
      </text>
      <CaptionLine d="M 450 386 L 448 322" />
      <text x="752" y="390" textAnchor="end">
        Ethernet (from step 1)
      </text>
      <CaptionLine d="M 712 380 L 700 368" />
    </g>
  </SceneSvg>
);

/** Scene 3 (custom hardware) — a terminal on the device types the one-line installer. */
const InstallerScene = () => (
  <SceneSvg label="Animation: running the one-line installer in the device terminal">
    <g transform="translate(300 90) scale(0.82)">
      <EdgeDevice led="on" />
    </g>
    <g className={styles.deviceInk}>
      <rect
        x="46"
        y="44"
        width="300"
        height="150"
        rx="10"
        strokeWidth={2}
        fill="var(--color-bg, #fff)"
      />
      <line x1="46" y1="76" x2="346" y2="76" strokeWidth={1.5} />
      <circle cx="64" cy="60" r="3" fill="currentColor" stroke="none" opacity={0.5} />
      <circle cx="78" cy="60" r="3" fill="currentColor" stroke="none" opacity={0.5} />
      <circle cx="92" cy="60" r="3" fill="currentColor" stroke="none" opacity={0.5} />
      <path d="M 64 104 l 10 8 l -10 8" strokeWidth={2.5} strokeLinecap="round" fill="none" />
      <rect
        className={styles.termTyped}
        x="86"
        y="108"
        width="200"
        height="7"
        rx="3.5"
        fill="currentColor"
        stroke="none"
        opacity={0.75}
      />
      <rect
        className={styles.termCursor}
        x="292"
        y="102"
        width="5"
        height="20"
        fill="currentColor"
        stroke="none"
      />
      <rect
        x="64"
        y="140"
        width="150"
        height="6"
        rx="3"
        fill="currentColor"
        stroke="none"
        opacity={0.25}
      />
      <rect
        x="64"
        y="156"
        width="110"
        height="6"
        rx="3"
        fill="currentColor"
        stroke="none"
        opacity={0.25}
      />
    </g>
    <CaptionLine d="M 240 196 L 330 240" />
    <g className={styles.sceneLabels}>
      <text x="46" y="228">
        A keyboard and screen on the device, or SSH
      </text>
    </g>
  </SceneSvg>
);

/** Scene 4 — a browser window on the store network opens the device's setup panel. */
const PanelScene = () => (
  <SceneSvg label="Animation: opening the device setup panel in a browser">
    <g transform="translate(330 110) scale(0.78)">
      <EdgeDevice led="on" />
    </g>
    <g className={styles.browserPop}>
      <g className={styles.deviceInk}>
        <rect
          x="46"
          y="40"
          width="320"
          height="190"
          rx="10"
          strokeWidth={2}
          fill="var(--color-bg, #fff)"
        />
        <line x1="46" y1="74" x2="366" y2="74" strokeWidth={1.5} />
        <circle cx="64" cy="57" r="3" fill="currentColor" stroke="none" opacity={0.5} />
        <circle cx="78" cy="57" r="3" fill="currentColor" stroke="none" opacity={0.5} />
        <rect x="94" y="48" width="240" height="18" rx="9" strokeWidth={1.2} />
      </g>
      <text className={styles.sceneMono} x="106" y="61">
        shobdo-edge.local:8088
      </text>
      <g className={styles.deviceInk}>
        <rect
          x="70"
          y="94"
          width="130"
          height="10"
          rx="5"
          fill="currentColor"
          stroke="none"
          opacity={0.5}
        />
        <rect x="70" y="118" width="272" height="26" rx="6" strokeWidth={1.2} />
        <rect x="70" y="156" width="272" height="26" rx="6" strokeWidth={1.2} />
        <path
          className={styles.panelCheck}
          d="M 84 202 l 8 8 l 16 -16"
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          pathLength={100}
        />
      </g>
      <text className={styles.sceneMono} x="80" y="135" opacity={0.55}>
        Panel password
      </text>
      <text className={styles.sceneMono} x="80" y="173" opacity={0.55}>
        Edge agent API key
      </text>
    </g>
    {/* Same-network link between browser and device */}
    <CaptionLine d="M 370 220 C 420 260, 440 270, 470 286" />
    <g className={styles.sceneLabels}>
      <text x="46" y="260">
        Any phone or computer on the store's wifi / network
      </text>
    </g>
  </SceneSvg>
);

/** Full-panel animated illustration for one install instruction. */
export const InstallScene = ({ kind }: { kind: InstallSceneKind }) => {
  if (kind === "router") return <RouterScene />;
  if (kind === "power") return <PowerScene />;
  if (kind === "installer") return <InstallerScene />;
  return <PanelScene />;
};
